import { useState, useEffect, useMemo } from 'react';
import { X, Sparkles } from 'lucide-react';

const THEMES = [
  { name: 'sunrise', gradient: 'from-amber-200 via-orange-200 to-yellow-100', accent: 'text-amber-900', overlay: 'bg-amber-50/30' },
  { name: 'forest', gradient: 'from-emerald-200 via-green-200 to-teal-100', accent: 'text-emerald-900', overlay: 'bg-emerald-50/30' },
  { name: 'ocean', gradient: 'from-sky-200 via-blue-200 to-cyan-100', accent: 'text-sky-900', overlay: 'bg-sky-50/30' },
  { name: 'lavender', gradient: 'from-violet-200 via-purple-200 to-fuchsia-100', accent: 'text-violet-900', overlay: 'bg-violet-50/30' },
  { name: 'rose', gradient: 'from-rose-200 via-pink-200 to-red-100', accent: 'text-rose-900', overlay: 'bg-rose-50/30' },
];

const CelebrationModal = ({ open, onClose, quote }) => {
  const [visible, setVisible] = useState(false);
  const theme = useMemo(() => THEMES[Math.floor(Math.random() * THEMES.length)], [open]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center" data-testid="celebration-modal">
      {/* Backdrop */}
      <div className={`absolute inset-0 bg-black/20 transition-opacity duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />

      {/* Modal sliding up from bottom */}
      <div
        className={`relative w-full max-w-lg mx-4 mb-0 transition-all duration-700 ease-out ${
          visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        <div className={`bg-gradient-to-t ${theme.gradient} rounded-t-3xl p-8 pb-12 shadow-2xl`}>
          {/* Close button */}
          <button onClick={onClose} className="absolute top-4 left-4 p-2 rounded-full bg-white/50 hover:bg-white/70 transition-colors" data-testid="close-celebration">
            <X className="w-5 h-5 text-gray-600" />
          </button>

          {/* Sparkle decoration */}
          <div className="flex justify-center mb-6">
            <div className={`p-4 rounded-full ${theme.overlay}`}>
              <Sparkles className={`w-10 h-10 ${theme.accent} animate-pulse`} />
            </div>
          </div>

          {/* Success message */}
          <p className={`text-center text-sm font-medium ${theme.accent} opacity-70 mb-4`}>تم إرسال تقريرك بنجاح</p>

          {/* Quote text */}
          <div className="text-center px-4">
            <p className={`font-bold leading-relaxed ${theme.accent} whitespace-pre-wrap`} dir="rtl" data-testid="celebration-quote-text"
               style={{ fontSize: 'clamp(0.9rem, 4vw, 1.5rem)' }}>
              {quote?.text || 'أحسنت! استمر في القراءة'}
            </p>
            {quote?.author && (
              <p className={`mt-4 text-sm font-medium ${theme.accent} opacity-60`}>- {quote.author}</p>
            )}
          </div>

          {/* Decorative dots */}
          <div className="flex justify-center gap-1.5 mt-8">
            {[...Array(5)].map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full ${theme.accent} bg-current opacity-20`} style={{ animationDelay: `${i * 0.2}s` }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CelebrationModal;
